import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, CssBaseline, GlobalStyles, MenuItem, TextField, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getStoredThemeMode, getTheme } from './theme';
import ThemeToggle from './components/ThemeToggle';
import { mishtameretRegister } from './mishtameretPortalApi';

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700&display=swap';

const EMPTY_FORM = {
  personalCode: '',
  idNumber: '',
  lastName: '',
  firstName: '',
  city: '',
  phone: '',
  email: '',
  occupation: '',
};

const OCCUPATIONS = ['עמל הפרנסה', 'כולל', 'ישיבת היכל התורה'];
const USA_CODE_LENGTH = 10;
const REGIONS = ['il', 'usa'];

/** ארץ / חו״ל לפי אזור הזמן של הדפדפן, ואם אין — לפי שפת הדפדפן. ברירת מחדל: ארץ */
function detectRegion() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz === 'Asia/Jerusalem' || tz === 'Asia/Tel_Aviv') return 'il';
    if (/^(America|US|Canada)\//.test(tz)) return 'usa';
  } catch {
    // דפדפן ללא Intl — ממשיכים לבדיקת השפה
  }
  const langs = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || [];
  if (langs.some((l) => /^(he|iw)\b/i.test(l || ''))) return 'il';
  if (langs.some((l) => /-(US|CA)$/i.test(l || ''))) return 'usa';
  return 'il';
}

/** ?region=usa / ?region=il בקישור גובר על הזיהוי האוטומטי */
function initialRegion(forced) {
  if (REGIONS.includes(forced)) return forced;
  try {
    const q = new URLSearchParams(window.location.search).get('region');
    if (REGIONS.includes(q)) return q;
  } catch {
    // ignore
  }
  return detectRegion();
}

/**
 * טופס רישום פתוח למשתמרת — קישור אחד שנשלח לגבאים לרישום חברים ללא התערבות המשרד.
 * הבקשה נשמרת כממתינה ומאושרת במסך "בקשות רישום ממתינות".
 * הארץ מזוהה אוטומטית, ואפשר להחליף בבורר שבראש הטופס.
 *
 * ארץ   — ת״ז + סטטוס (עברית בלבד). ת״ז קיימת → בקשת עדכון פרטים.
 * חו״ל — קוד אישי לבחירה, 10 ספרות (עברית + אנגלית).
 *
 * @param {{ region?: 'il' | 'usa' }} props region — קישור ישן (/register/usa) שקובע ארץ מראש
 */
const RegisterPage = ({ region: forcedRegion }) => {
  const [region, setRegion] = useState(() => initialRegion(forcedRegion));
  const isUsa = region === 'usa';
  const [mode, setMode] = useState(() => getStoredThemeMode());
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const theme = getTheme(mode);

  // תוויות: בארץ עברית בלבד, בחו״ל עברית + אנגלית
  const t = (he, en) => (isUsa ? `${he} / ${en}` : he);

  useEffect(() => {
    let link = document.getElementById('mishtameret-fonts');
    if (!link) {
      link = document.createElement('link');
      link.id = 'mishtameret-fonts';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = FONT_HREF;
    document.documentElement.style.background = theme.htmlBg;
    document.body.style.background = theme.htmlBg;
  }, [theme.htmlBg]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const buildPayload = () => {
    const base = {
      firstName: form.firstName,
      lastName: form.lastName,
      city: form.city,
      phone: form.phone,
      email: form.email,
    };
    if (isUsa) {
      const code = form.personalCode.replace(/\D/g, '');
      if (code.length !== USA_CODE_LENGTH) {
        return { error: `הקוד האישי חייב להיות בן ${USA_CODE_LENGTH} ספרות / Code must be ${USA_CODE_LENGTH} digits` };
      }
      return { payload: { ...base, region: 'usa', personalCode: code } };
    }
    const id = form.idNumber.replace(/\D/g, '');
    if (id.length < 5 || id.length > 9) {
      return { error: 'נא להזין מספר תעודת זהות תקין' };
    }
    if (!form.occupation) {
      return { error: 'יש לבחור סטטוס' };
    }
    return { payload: { ...base, region: 'il', idNumber: id, occupation: form.occupation } };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.firstName.trim() || !form.lastName.trim() || !form.city.trim() || !form.phone.trim()) {
      setError(t('נא למלא את כל השדות', 'Please fill in all fields'));
      return;
    }
    const { payload, error: validationError } = buildPayload();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    try {
      const data = await mishtameretRegister(payload);
      setDone({ update: Boolean(data?.update) });
    } catch (err) {
      const serverMsg = typeof err.response?.data?.error === 'string' ? err.response.data.error : '';
      if (serverMsg) setError(serverMsg);
      else if (!err.response) {
        setError(t(
          'לא ניתן להתחבר לשרת. בדקו את החיבור לאינטרנט, ואם יש סינון — שהאתר מאושר בו.',
          'Could not reach the server. Check your connection or internet filter.'
        ));
      } else setError(t('השליחה נכשלה. נסו שוב.', 'Submission failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const changeRegion = (next) => {
    if (next === region) return;
    setRegion(next);
    setError('');
  };

  const registerAnother = () => {
    setForm(EMPTY_FORM);
    setError('');
    setDone(null);
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: theme.inputBg,
      color: theme.text,
      borderRadius: 2,
      fontFamily: theme.fontBody,
      fontSize: '1.05rem',
      minHeight: 52,
      '& fieldset': { borderColor: theme.borderSoft },
      '&:hover fieldset': { borderColor: theme.border },
      '&.Mui-focused fieldset': { borderColor: theme.accent },
    },
    '& .MuiInputLabel-root': { color: theme.textMuted, fontFamily: theme.fontBody },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.accent },
    '& .MuiFormHelperText-root': { color: theme.textMuted, fontFamily: theme.fontBody },
    '& .MuiSelect-icon': { color: theme.textMuted },
  };

  const buttonSx = {
    mt: 1,
    minHeight: 52,
    borderRadius: 999,
    bgcolor: theme.accent,
    color: theme.accentContrast,
    fontFamily: theme.fontBody,
    fontWeight: 700,
    fontSize: '1.1rem',
    boxShadow: theme.shadow,
    '&:hover': { bgcolor: theme.accentSoft },
    '&.Mui-disabled': { bgcolor: theme.borderSoft, color: theme.textMuted },
  };


  return (
    <>
      <CssBaseline />
      <GlobalStyles
        styles={{
          body: { margin: 0, fontFamily: theme.fontBody },
          html: { background: theme.htmlBg },
        }}
      />
      <Box
        dir="rtl"
        sx={{
          minHeight: '100dvh',
          background: theme.pageBg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
          position: 'relative',
          fontFamily: theme.fontBody,
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, left: 16 }}>
          <ThemeToggle mode={mode} theme={theme} onToggle={() => setMode(mode === 'dark' ? 'light' : 'dark')} />
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            width: '100%',
            maxWidth: 440,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
          }}
        >
          <Box
            component="img"
            src={theme.logo}
            alt="משתמרת"
            sx={{
              width: { xs: 140, sm: 180 },
              height: 'auto',
              mb: 1,
              filter: mode === 'light' ? 'drop-shadow(0 4px 16px rgba(0,0,0,0.12))' : 'none',
            }}
          />

          <Typography
            component="h1"
            sx={{
              fontFamily: theme.fontDisplay,
              fontWeight: 700,
              fontSize: { xs: '1.9rem', sm: '2.2rem' },
              color: theme.text,
              lineHeight: 1.25,
              textAlign: 'center',
            }}
          >
            {isUsa ? 'טופס רישום משתמרת' : 'הרשמה ועדכון פרטים — משתמרת'}
          </Typography>

          {done ? (
            <>
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: theme.accent }} />
              <Typography sx={{ color: theme.text, textAlign: 'center', fontSize: '1.15rem', fontWeight: 600 }}>
                {done.update
                  ? 'בקשת עדכון הפרטים התקבלה ותועבר לאישור המשרד'
                  : 'הבקשה התקבלה ותועבר לאישור המשרד'}
              </Typography>
              {isUsa ? (
                <Typography sx={{ color: theme.textMuted, textAlign: 'center' }} dir="ltr">
                  Your registration was received and will be approved by the office.
                </Typography>
              ) : null}
              <Button fullWidth onClick={registerAnother} sx={buttonSx}>
                {t('רישום נוסף', 'Register another')}
              </Button>
            </>
          ) : (
            <>
              <Box
                role="radiogroup"
                aria-label="ארץ / Country"
                sx={{
                  display: 'flex',
                  width: '100%',
                  p: 0.5,
                  gap: 0.5,
                  borderRadius: 999,
                  bgcolor: theme.inputBg,
                  border: `1px solid ${theme.borderSoft}`,
                }}
              >
                {[
                  { value: 'il', label: 'בארץ ישראל' },
                  { value: 'usa', label: 'בחו״ל / Abroad' },
                ].map((opt) => {
                  const selected = region === opt.value;
                  return (
                    <Box
                      key={opt.value}
                      component="button"
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => changeRegion(opt.value)}
                      sx={{
                        flex: 1,
                        minHeight: 44,
                        border: 0,
                        borderRadius: 999,
                        cursor: 'pointer',
                        fontFamily: theme.fontBody,
                        fontSize: '1rem',
                        fontWeight: selected ? 700 : 500,
                        bgcolor: selected ? theme.accent : 'transparent',
                        color: selected ? theme.accentContrast : theme.textMuted,
                        transition: 'background-color .15s, color .15s',
                        '&:focus-visible': { outline: `2px solid ${theme.accent}`, outlineOffset: 2 },
                      }}
                    >
                      {opt.label}
                    </Box>
                  );
                })}
              </Box>

              {isUsa ? (
                <Typography sx={{ color: theme.textMuted, textAlign: 'center', mb: 1 }}>
                  נא למלא את הפרטים בעברית
                  <br />
                  <span dir="ltr">Please write the details in Hebrew</span>
                </Typography>
              ) : null}

              {isUsa ? (
                <TextField
                  fullWidth
                  label="קוד אישי / Personal code"
                  helperText={`בחרו קוד בן ${USA_CODE_LENGTH} ספרות לכניסה למערכת / Choose a ${USA_CODE_LENGTH}-digit code`}
                  value={form.personalCode}
                  onChange={setField('personalCode')}
                  inputProps={{ inputMode: 'numeric', autoComplete: 'off', dir: 'ltr', maxLength: USA_CODE_LENGTH }}
                  sx={fieldSx}
                />
              ) : (
                <TextField
                  fullWidth
                  label="מספר תעודת זהות"
                  helperText="9 ספרות כולל ספרת ביקורת"
                  value={form.idNumber}
                  onChange={setField('idNumber')}
                  inputProps={{ inputMode: 'numeric', autoComplete: 'off', dir: 'ltr', maxLength: 9 }}
                  sx={fieldSx}
                />
              )}
              <TextField
                fullWidth
                label={t('שם משפחה', 'Last name')}
                value={form.lastName}
                onChange={setField('lastName')}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                label={t('שם פרטי', 'First name')}
                value={form.firstName}
                onChange={setField('firstName')}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                label={t('עיר', 'City')}
                value={form.city}
                onChange={setField('city')}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                label={isUsa ? 'מספר טלפון / Phone number' : 'טלפון (נייד או נייח)'}
                value={form.phone}
                onChange={setField('phone')}
                inputProps={{ inputMode: 'tel', autoComplete: 'tel', dir: 'ltr' }}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                type="email"
                label={isUsa ? 'מייל (רשות) / Email (optional)' : 'מייל (רשות)'}
                helperText={isUsa ? 'לקבלת הציונים במייל / To receive your scores by email' : 'לקבלת הציונים במייל'}
                value={form.email}
                onChange={setField('email')}
                inputProps={{ inputMode: 'email', autoComplete: 'email', dir: 'ltr' }}
                sx={fieldSx}
              />

              {isUsa ? null : (
                <TextField
                  select
                  fullWidth
                  label="סטטוס"
                  value={form.occupation}
                  onChange={setField('occupation')}
                  sx={fieldSx}
                >
                  {OCCUPATIONS.map((o) => (
                    <MenuItem key={o} value={o}>{o}</MenuItem>
                  ))}
                </TextField>
              )}

              {error ? (
                <Typography sx={{ color: theme.danger, width: '100%', textAlign: 'center' }}>
                  {error}
                </Typography>
              ) : null}

              <Button type="submit" fullWidth disabled={loading} sx={buttonSx}>
                {loading
                  ? <CircularProgress size={26} sx={{ color: theme.accentContrast }} />
                  : (isUsa ? 'שליחה / Submit' : 'שמירה ועדכון')}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default RegisterPage;
